// ─── Product Analytics & Error Monitoring ─────────────────────────────────────
// PostHog for product analytics (free tier: 1M events/month)
// Sentry for error monitoring (free tier: 5K errors/month)
//
// To activate:
// 1. Create PostHog account → get project API key → set VITE_POSTHOG_KEY env var
// 2. Create Sentry account → get DSN → set VITE_SENTRY_DSN env var

let posthogLoaded = false;
let sentryLoaded = false;

// ─── PostHog Analytics ────────────────────────────────────────────────────────

export function initAnalytics() {
  const key = import.meta.env.VITE_POSTHOG_KEY;
  if (!key || posthogLoaded) return;

  try {
    // Load PostHog script
    const script = document.createElement("script");
    script.innerHTML = `
      !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
      posthog.init('${key}',{api_host:'https://eu.i.posthog.com', person_profiles: 'identified_only', capture_pageview: false});
    `;
    document.head.appendChild(script);
    posthogLoaded = true;
  } catch (e) {
    console.warn("PostHog init failed:", e);
  }
}

// Track a page view
export function trackPageView(pageName) {
  if (!posthogLoaded || !window.posthog) return;
  try { window.posthog.capture("$pageview", { page: pageName }); } catch {}
}

// Track a custom event
export function trackEvent(name, properties = {}) {
  if (!posthogLoaded || !window.posthog) return;
  try { window.posthog.capture(name, properties); } catch {}
}

// Identify a user (call after login)
export function identifyUser(userId, traits = {}) {
  if (!posthogLoaded || !window.posthog) return;
  try { window.posthog.identify(userId, traits); } catch {}
}

// Reset identity (call on logout)
export function resetAnalytics() {
  if (!posthogLoaded || !window.posthog) return;
  try { window.posthog.reset(); } catch {}
}

// ─── Sentry Error Monitoring ──────────────────────────────────────────────────
//
// Sentry's browser SDK is loaded via CDN to keep the main bundle tiny. The
// loader is async, which opens a race window where early crashes (during
// React mount or the first render) would vanish. We close that window with a
// pre-load queue:
//
//   1. Install a `window.onerror` + `unhandledrejection` handler
//      immediately — BEFORE the Sentry script loads. Events land in
//      `_errorQueue`.
//   2. Also buffer manual `captureError()` calls.
//   3. When Sentry's script fires `onload`, init the SDK and replay every
//      queued event.
//
// This means a crash during first paint still reaches Sentry, which was the
// audit gap flagged as "Browser-side Sentry half-baked".

const _errorQueue = [];

function _queueError(error, context = {}) {
  try { _errorQueue.push({ error, context, ts: Date.now() }); } catch {}
  // Cap the queue — in pathological loops we don't want to OOM the tab.
  if (_errorQueue.length > 100) _errorQueue.splice(0, _errorQueue.length - 100);
}

function _installPreloadErrorHandlers() {
  if (typeof window === "undefined" || window.__chPreloadErrorHandlersInstalled) return;
  window.__chPreloadErrorHandlersInstalled = true;

  window.addEventListener("error", (evt) => {
    const err = evt?.error || new Error(evt?.message || "Unknown window.error");
    _queueError(err, {
      source: "window.onerror",
      filename: evt?.filename, lineno: evt?.lineno, colno: evt?.colno,
    });
  });
  window.addEventListener("unhandledrejection", (evt) => {
    const reason = evt?.reason;
    const err = reason instanceof Error ? reason
      : new Error(typeof reason === "string" ? reason : JSON.stringify(reason));
    _queueError(err, { source: "unhandledrejection" });
  });
}

function _flushQueue() {
  if (!window?.Sentry?.captureException) return;
  while (_errorQueue.length > 0) {
    const { error, context } = _errorQueue.shift();
    try { window.Sentry.captureException(error, { extra: context }); } catch {}
  }
}

export function initErrorMonitoring() {
  // Always install the queue — even without a DSN, it prevents the
  // eventual Sentry init from missing early crashes. Cheap, idempotent.
  _installPreloadErrorHandlers();

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || sentryLoaded) return;

  try {
    const script = document.createElement("script");
    script.src = "https://browser.sentry-cdn.com/8.0.0/bundle.min.js";
    script.crossOrigin = "anonymous";
    script.onload = () => {
      if (window.Sentry) {
        window.Sentry.init({
          dsn,
          environment: import.meta.env.MODE || "production",
          tracesSampleRate: 0.1,
          replaysSessionSampleRate: 0.01,
          replaysOnErrorSampleRate: 0.5,
        });
        sentryLoaded = true;
        _flushQueue();
      }
    };
    script.onerror = () => {
      // CDN failed to load — nothing we can do, errors will be lost.
      console.warn("[analytics] Sentry CDN failed to load; errors will not be reported.");
    };
    document.head.appendChild(script);
  } catch (e) {
    console.warn("Sentry init failed:", e);
  }
}

// Capture an error manually. Always logs to console; sends to Sentry if
// ready, otherwise queues for replay once Sentry finishes loading.
export function captureError(error, context = {}) {
  try {
    if (sentryLoaded && window.Sentry?.captureException) {
      window.Sentry.captureException(error, { extra: context });
    } else {
      _queueError(error, context);
    }
  } catch {}
  console.error(error);
}

// Set user context for error reports
export function setErrorUser(userId, email) {
  if (sentryLoaded && window.Sentry) {
    try { window.Sentry.setUser({ id: userId, email }); } catch {}
  }
}
